import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    BudgetProposalT,
    CellRendererParams,
} from 'routes/BudgetModule/config/types';
import { getIsEditablePhase } from 'routes/BudgetModule/shared/phases/helpers';
import TreeTablePicker from 'components/inputs/TreeTablePicker/TreeTablePicker';
import { optionsToFlatTree } from 'components/inputs/TreeTablePicker/helpers';
import { SelectionTreeT } from 'business/definitions';
import Loading from 'components/Loading';
import { styled } from '@mui/system';
import { capitalize } from 'lodash';
import { sortTreeByChildrenLabel } from 'helpers/treeHelpers';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { translateBlock } from 'i18n';
import { getBudgetProposalBy } from '../../helpers';
import { useModalOptionsContext } from '../optionsContext';
import { LoadingBudgetProposalT } from './gridHelpers';
import { useMutateResourceCanEdit } from './hooks';
import { BudgetProposalByResource } from './types';
import { trackEventResourcesAuthorizedChanged } from '../../../trackersHelpers';
import { TFunction, useTranslation } from 'react-i18next';
import { getClientBind } from 'store/APIClient';

const DisablingTooltip = (props: TooltipProps & { disabled: boolean }) => {
    const { disabled, children } = props;
    if (disabled) {
        return children;
    }

    return (
        <Tooltip {...props}>
            <div style={{ width: '100%' }}>{children}</div>
        </Tooltip>
    );
};

const StyTreeTablePicker = styled(TreeTablePicker, {
    name: 'StyTreeTablePicker',
})({
    display: 'flex',
    width: '100%',
    '& .MuiInputBase-root': {
        justifyContent: 'space-between',
    },
});

const getSelectedValuesToTree = (
    selectedValues: string[],
    allValues: BudgetProposalByResource[],
    resourceNameMapping: Record<string, string>,
) => {
    const allValuesSoftwareIds: string[] = allValues.map((r) => r.softwareId);
    return selectedValues
        .filter((s) => allValuesSoftwareIds.includes(s))
        .map((s) => ({
            value: s,
            label: resourceNameMapping[s],
            isBadValue: false,
            isSubtreeSelected: false,
        }));
};

const getNameOrDefault = (r: BudgetProposalByResource, t: TFunction) =>
    r.softwareId === null || r.softwareId === '' ? t('cellar.blanks') : r.name;

export default function CanResourceEditCellRenderer({
    data,
    value,
}: CellRendererParams<
    LoadingBudgetProposalT,
    BudgetProposalT['canResourceEdit']
>): React.ReactElement {
    const { resourceOptions } = useModalOptionsContext();
    const { t } = useTranslation();

    const bind = getClientBind();
    const isEditable = data?.phase && getIsEditablePhase(data.phase);
    const title = t('adminBudgetSpace.inscriptionEdition', {
        authorized: bind === 'ampmetropole' ? 'autorisées' : 'autorisés',
        resource: capitalize(translateBlock(getBudgetProposalBy(data!))),
    });

    const allResources = useMemo(() => {
        if (!resourceOptions || !data?.id) {
            return [];
        }

        const resources = resourceOptions[data.id];
        if (resources) {
            return resources.map((r) => ({
                ...r,
                softwareId: r.softwareId === null ? '' : r.softwareId,
            }));
        }

        return [];
    }, [resourceOptions]);

    const allOptions = useMemo(
        () =>
            sortTreeByChildrenLabel(
                optionsToFlatTree(
                    allResources.map((r) => ({
                        value: r.softwareId,
                        label: getNameOrDefault(r, t),
                    })),
                ),
            ),
        [JSON.stringify(allResources)],
    );

    const resourceNameMapping: Record<string, string> = useMemo(
        () =>
            allResources.reduce(
                (mapping, resource) =>
                    Object.assign(mapping, {
                        [resource.softwareId]: getNameOrDefault(resource, t),
                    }),
                {},
            ),
        [JSON.stringify(allResources)],
    );
    const [selectedValues, setSelectedValues] = useState<SelectionTreeT[]>([]);
    useEffect(() => {
        if (value) {
            setSelectedValues(
                getSelectedValuesToTree(
                    value,
                    allResources,
                    resourceNameMapping,
                ),
            );
        }
    }, [allResources, data]);

    const { mutate: updateResourceCanEdit } = useMutateResourceCanEdit(data!);

    const onUpdateResources = useCallback(
        (selected: SelectionTreeT[]) => {
            const softwareIds = selected.map((s) =>
                s.value === '' ? null : s.value,
            );
            updateResourceCanEdit(softwareIds);
            trackEventResourcesAuthorizedChanged(
                data?.id,
                data?.name,
                softwareIds,
            );
        },
        [updateResourceCanEdit],
    );

    if (resourceOptions === undefined || data === undefined) {
        return <Loading />;
    }
    const budgetPhaseLabel = t(`budgetProposal.${data?.phase}`);
    const tooltipTitle = `${t(
        'adminBudgetSpace.inscriptionEditionDisabled',
    )}${budgetPhaseLabel}`;

    return (
        <DisablingTooltip title={tooltipTitle} disabled={isEditable ?? false}>
            <StyTreeTablePicker
                variant="outlined"
                gridOptions={allOptions}
                selectedValues={selectedValues}
                isLoading={false}
                onChange={onUpdateResources}
                disabled={!isEditable}
                scopeOptions={[]}
                title={title}
                inputLabel={title}
                showFilterPrefixOnDialogTitle={false}
                doNotSort
            />
        </DisablingTooltip>
    );
}
